import { createClient } from "@supabase/supabase-js";
import { Database } from "./schema";

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error("Missing Supabase environment variables");
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

// ====================================================
// Database Schema Types (clean + developer-friendly)
// ====================================================

// ---------------------- Discounts ----------------------
export interface Discount {
  id: string;
  code: string;
  description?: string;
  discount_type: "percentage" | "fixed";
  amount: number;
  start_date?: string | null;
  end_date?: string | null;
  usage_limit_per_customer?: number | null;
  total_usage_limit?: number | null;
  current_total_usage?: number;
  is_stackable?: boolean;
  is_active?: boolean;
  created_at?: string;
  updated_at?: string;

  // relations
  customer_usages?: DiscountCustomerUsage[];
  shipment_discounts?: ShipmentDiscount[];
}

// ---------------------- Promo Codes ----------------------
export interface PromoCode {
  id: string;
  code: string;
  description?: string;
  promo_type: "percentage" | "fixed";
  amount: number;
  start_date: string | null;
  end_date: string | null;
  usage_limit_per_customer?: number | null;
  total_usage_limit?: number | null;
  current_total_usage?: number;
  is_stackable?: boolean;
  is_active?: boolean;
  created_at?: string;
  updated_at?: string;

  // relations
  usages?: PromoCodeUsage[];
  shipment_discounts?: ShipmentDiscount[];
}

// ---------------------- Discount Customer Usage ----------------------
export interface DiscountCustomerUsage {
  id: string;
  code_id?: string;
  customer_id?: string;
  usage_limit?: number | null;
  usage_count?: number;
  created_at?: string;
  updated_at?: string;

  // relations
  discount?: Discount;
  customer?: Customer;
}

// ---------------------- Promo Code Usage ----------------------
export interface PromoCodeUsage {
  id: string;
  code_id?: string;
  customer_id?: string;
  usage_count?: number;
  created_at?: string;
  updated_at?: string;

  // relations
  promo?: PromoCode;
  customer?: Customer;
}

// ---------------------- Shipment Discounts ----------------------
export interface ShipmentDiscount {
  id: string;
  shipment_id: string;
  discount_id?: string | null;
  promo_id?: string | null;
  applied_order: number;
  created_at?: string;

  // relations
  shipment?: Shipment;
  discount?: Discount;
  promo?: PromoCode;
}

// ---------------------- Customers ----------------------
export type Customer = Database["public"]["Tables"]["customers"]["Row"] & {
  addresses?: Address[] | null;
  shipments?: Shipment[] | null;
};
export type InsertCustomer =
  Database["public"]["Tables"]["customers"]["Insert"];

// ---------------------- Shipments ----------------------
export type Shipment = Database["public"]["Tables"]["shipments"]["Row"] & {
  customer?: Customer | null;
  packages?: ShipmentPackage[] | null;
  codes?: ShipmentDiscount[] | null;
  delivery_address_ref?: Address | null;
};
export type InsertShipment =
  Database["public"]["Tables"]["shipments"]["Insert"];

// ---------------------- Shipment Packages ----------------------
export interface ShipmentPackage {
  id: string;
  shipment_id?: string | null;

  dimensions?: string | null;
  dimensional_weight?: number | null;
  scale_weight?: number | null;
  is_weight_included?: boolean | null;

  created_at?: string | null;

  shipment?: Shipment | null;
  items?: PackageItem[] | null;
}

// ---------------------- Package Items ----------------------
export interface PackageItem {
  id: string;
  item_name: string;
  quantity?: number | null;
  item_description?: string | null;
  category_id?: string | null;
  shipment_package_id?: string | null;
  is_weight_included?: boolean | null;
  total_value?: number | null;
  total_weight?: number | null;
  created_at?: string | null;

  category?: Category;
  package?: ShipmentPackage | null;
}

// Temporary alias for backwards compatibility with legacy shipment item naming
// TODO: Remove once all references are migrated to PackageItem
export type ShipmentItem = PackageItem;

/**
 * Represents a shipment category relationship.
 * This links a shipment to a specific category and defines any extra charge rules.
 */
export interface ShipmentCategory {
  id: string;
  shipment_id?: string;
  category_id?: string;
  created_at?: string;
  updated_at?: string;

  // relations
  shipment?: Shipment;
  category?: Category;
}

/**
 * Represents a category with optional extra charge configurations.
 */
export interface Category {
  id: string;
  name: string;
  slug: string;
  description?: string;
  extra_charge_type?:
    | "fixed"
    | "fixed_range"
    | "percentage"
    | "percentage_range";
  extra_charge_reason?: string;
  min_amount?: number;
  max_amount?: number;
  created_at?: string;
  updated_at?: string;

  // relations
  shipment_categories?: ShipmentCategory[];
}

/**
 * Represents a shipment category relationship.
 * This links a shipment to a specific category and defines any extra charge rules.
 */
export interface ShipmentCategory {
  id: string;
  shipment_id?: string;
  category_id?: string;
  created_at?: string;
  updated_at?: string;

  // relations
  shipment?: Shipment;
  category?: Category;
}

/**
 * Represents a category with optional extra charge configurations.
 */
export interface Category {
  id: string;
  name: string;
  slug: string;
  description?: string;
  extra_charge_type?:
    | "fixed"
    | "fixed_range"
    | "percentage"
    | "percentage_range";
  extra_charge_reason?: string;
  min_amount?: number;
  max_amount?: number;
  created_at?: string;
  updated_at?: string;

  // relations
  shipment_categories?: ShipmentCategory[];
}

// ---------------------- Admins ----------------------
export interface Admin {
  id: string;
  name: string;
  email: string;
  role?: "admin" | "super_admin";
  department?: string;
  phone?: string;
  is_active?: boolean;
  last_login?: string;
  created_at?: string;
}

// ---------------------- Settings ----------------------
export interface Settings {
  id: string;
  key: string;
  value: string;
  category?: string;
  description?: string;
  is_public?: boolean;
  updated_at?: string;
}

// ---------------------- Addresses ----------------------
export interface Address {
  id: string;
  customer_id?: string;
  address_type: "us_pickup" | "haiti_delivery" | "billing";
  label?: string;
  street_address: string;
  city: string;
  state_province?: string;
  postal_code?: string;
  country: string;
  is_default?: boolean;
  created_at?: string;
}

// ---------------------- Tracking Events ----------------------
export interface TrackingEvent {
  id: string;
  shipment_id?: string;
  status: string;
  location?: string;
  description?: string;
  event_timestamp?: string;
  created_by?: string;
  is_public?: boolean;
  created_at?: string;
}

// ---------------------- Notifications ----------------------
export interface Notification {
  id: string;
  recipient_id: string;
  recipient_type: "customer" | "admin";
  notification_type:
    | "shipment_update"
    | "delivery_ready"
    | "payment_due"
    | "system_alert";
  title: string;
  message: string;
  shipment_id?: string;
  is_read?: boolean;
  sent_via?: "email" | "sms" | "push" | "in_app";
  sent_at?: string;
  created_at?: string;
}

// ---------------------- Invoices ----------------------
export interface Invoice {
  id: string;
  invoice_number: string;
  customer_id?: string;
  shipment_id?: string;
  subtotal: number;
  tax_amount?: number;
  total_amount: number;
  status?: "pending" | "paid" | "overdue" | "cancelled";
  due_date?: string;
  paid_date?: string;
  payment_method?: string;
  notes?: string;
  created_at?: string;
}

// ---------------------- Pricing Tiers ----------------------
export interface PricingTier {
  id: string;
  tier_name: string;
  min_weight: number;
  max_weight?: number;
  rate: number;
  tier_order?: number;
  is_active?: boolean;
  created_at?: string;
}
